import { LoadingButton, LoadingButtonProps } from '@mui/lab';
import { Icon, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import Button, { ButtonProps } from '@mui/material/Button';
import { PrimitiveAtom, useAtom } from 'jotai';
import { CSSProperties, FC, useCallback, useState } from 'react';
import { ZINDEX } from '../../../constants/styles';
import { colors } from '../../../theme/colors';
import { currentStepStateFinanceJ } from './TransactionFormUIStateJ';

export interface IStepperFormButtonsProps {
  backButtonLabel?: string;
  stepIsValid: () => Promise<boolean>;
  nextButtonLabel?: string;
  nextDisabled?: boolean;
  handleGoBack?: () => void;
  secondaryActionProps?: {
    label: string;
    handler: () => Promise<void>;
    buttonProps?: ButtonProps;
  };
  stepState?: PrimitiveAtom<number>;
  incrementOnNext?: boolean;
  style?: CSSProperties;
}

export function StepperFormButtons(props: IStepperFormButtonsProps) {
  const {
    backButtonLabel = 'Previous',
    nextButtonLabel = 'Next',
    nextDisabled = false,
    stepIsValid,
    handleGoBack,
    secondaryActionProps,
    stepState = currentStepStateFinanceJ,
    incrementOnNext = true,
    style,
  } = props;
  const [currentStep, setCurrentStep] = useAtom(stepState);
  const [loading, setLoading] = useState(false);
  const [loadingSecondary, setLoadingSecondary] = useState(false);

  const handleNext = useCallback(async () => {
    setLoading(true);
    const isValid = await stepIsValid();

    if (isValid && incrementOnNext) {
      setCurrentStep(currentStep + 1);
    }
    setLoading(false);
  }, [currentStep, incrementOnNext, setCurrentStep, stepIsValid]);

  const handleBack = () => {
    if (handleGoBack) {
      return handleGoBack();
    }
    setCurrentStep(currentStep - 1);
  };

  const handleSecondaryAction = useCallback(async () => {
    if (!secondaryActionProps) {
      return;
    }
    setLoadingSecondary(true);
    await secondaryActionProps.handler();
    setLoadingSecondary(false);
  }, [secondaryActionProps]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        pt: '2rem',
        pb: '1rem',
        position: 'sticky',
        bottom: 0,
        // Sticky needs something to sit on: without these the form scrolled visibly through the
        // buttons. Matches what StepperSx already does for the sticky header above.
        background: colors.primary[0],
        zIndex: ZINDEX.FORM_STEPPER,
      }}
      style={style}
    >
      {currentStep > 0 || Boolean(handleGoBack) ? (
        <Button
          color='secondary'
          variant='outlined'
          size='medium'
          type='button'
          onClick={handleBack}
          sx={{ mr: 1 }}
        >
          {backButtonLabel}
        </Button>
      ) : (
        <div />
      )}
      <Box sx={{ flex: '1 1 auto' }} />
      <Stack direction={'row'} gap='0.5rem'>
        {secondaryActionProps && (
          <ActionLoadingButton
            {...secondaryActionProps.buttonProps}
            loading={loadingSecondary}
            disabled={loadingSecondary}
            onClick={handleSecondaryAction}
          >
            {secondaryActionProps.label}
          </ActionLoadingButton>
        )}

        <ActionLoadingButton onClick={handleNext} loading={loading} disabled={loading || nextDisabled}>
          {nextButtonLabel}
        </ActionLoadingButton>
      </Stack>
    </Box>
  );
}

const ActionLoadingButton: FC<LoadingButtonProps> = (props) => {
  const { loading, children, onClick, ...rest } = props;
  return (
    <LoadingButton
      color='secondary'
      type='button'
      variant='contained'
      size='medium'
      loading={loading}
      disabled={!!loading}
      loadingPosition='start'
      startIcon={loading ? <Icon /> : <></>}
      onClick={onClick}
      {...rest}
    >
      {children}
    </LoadingButton>
  );
};
